import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import BodyText from 'LuxKit/Typography/BodyText'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import TextButton from 'LuxKit/Button/TextButton'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import { useAppSelector } from 'hooks/reduxHooks'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { addFlightPrice } from 'lib/offer/pricing'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'

const Root = styled.div`
  border: 1px solid ${props => props.theme.palette.highlight.secondary.normalForeground};
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const ContentContainer = styled.div`
  flex: 1;
  container-type: inline-size;
`

const Content = styled.div`
  display: flex;
  padding: ${rem(20)};
  gap: ${rem(20)};
  flex-direction: column;

  @container (min-width: 410px) {
    flex-direction: row;
    align-items: stretch;
  }
`

const ImageContainer = styled.div`
  width: ${rem(182)};
  flex-shrink: 0;
`

const DetailsColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const PricingColumn = styled(Group)`
  width: ${rem(170)};
  flex-shrink: 0;
`

interface Props {
  suggestedOption: App.OfferPackageOptionView
  message: string
  /** Fallback behaviour when clicking "View package", for Safari and other crap browsers that don't support `checkVisibility` */
  viewPackageFallback: () => void
}

function SuggestedOptionPreview({ suggestedOption, message, viewPackageFallback }: Props) {
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const { currentRegionCode } = useContext(GeoContext)
  const { offer, checkInDate, duration, charterBundleRoute, priceMode, rooms } = offerPageState
  const isSameRoomMode = useAppSelector(appState => !isMultipleRoomBookingActive(offerPageState, appState))

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  const pricing = suggestedOption.pricing
  const memberPricing = suggestedOption.memberPricing
  const pricingWithFlights = flightPrice ? addFlightPrice(pricing, flightPrice) : undefined
  const memberPricingWithFlights = (memberPricing && flightPrice) ? addFlightPrice(memberPricing, flightPrice) : undefined

  const forceBundle = !!(charterBundleRoute || offer?.bundledWithFlightsOnly)
  const pricingToShow = forceBundle ? pricingWithFlights : pricing
  const memberPricingToShow = forceBundle ? memberPricingWithFlights : memberPricing

  const pkg = suggestedOption.package
  const roomType = pkg.roomType
  const firstImage = roomType?.images?.[0]

  const { inclusions, bonusInclusions } = useOfferInclusions(pkg)
  const allInclusions = useMemo(() => [...bonusInclusions, ...inclusions], [bonusInclusions, inclusions])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView: suggestedOption,
      currentRegionCode,
      nights: duration,
    }),
  ], [offer, suggestedOption, currentRegionCode, duration])

  const scrollToPackage = useCallback(() => {
    // Can't just use scrollToId here, because there are actually two elements with the same ID for
    // each package - one for mobile and one for desktop.
    const anchors = document.querySelectorAll(`#${CSS.escape(roomType.id)}`)
    const target = [...anchors].find(el => el.checkVisibility?.())
    if (target) {
      target.scrollIntoView({ behavior: 'auto' })
    } else {
      viewPackageFallback()
    }
  }, [roomType.id, viewPackageFallback])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const nightsText = useMemo(() => {
    const nights = intl.formatMessage(
      {
        id: '1ZgK0L_Ngfs-n0KvQPTe',
        defaultMessage: '{count, plural, one {# night} other {# nights}}',
        description: 'Number of nights for price caption in the pricing or cost breakdown section',
      },
      { count: duration },
    )

    return checkInDate
      ? nights
      : intl.formatMessage(
          {
            id: 'dAvWpPRm0DQRe8XuTkn9',
            defaultMessage: '{nights} from',
            description: 'Price caption with number of nights and from text for prices without specific dates',
          },
          { nights },
        )
  }, [intl, duration, checkInDate])

  if (!offer || !roomType) return null

  return <Root>
    <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>{message}</MessageBanner>
    <Group direction="horizontal" verticalAlign="stretch">
      <ImageContainer>
        <HeightFillingImage
          id={firstImage?.id ?? NO_IMAGE_AVAILABLE_ID}
          width={182}
          mobileWidth="182px"
          fit="center"
          quality="good"
          dpr={2}
        />
      </ImageContainer>
      <ContentContainer>
        <Content>
          <DetailsColumn>
            <Group direction="vertical" gap={8}>
              <BodyText variant="large" weight="bold">{roomType.name}</BodyText>

              {offer.property && pkg.roomRate?.cancellationPolicy && (
                <FlashCancellationPolicies
                  checkInDate={checkInDate}
                  propertyTimezoneOffset={offer.property.timezoneOffset}
                  cancellationPolicy={pkg.roomRate.cancellationPolicy}
                />
              )}

              <LoyaltyPointsEarn
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={checkInDate ? 'exact' : 'estimate'}
                offer={offer}
              />

              {allInclusions.length > 0 && (
                <OfferInclusionsList
                  inclusions={allInclusions}
                  truncateAfter={2}
                  size="small"
                  onShowMoreClick={isSameRoomMode ? scrollToPackage : undefined}
                  showMoreText={intl.formatMessage({
                    id: 'pnzs4q516eNSNId9yp3-',
                    defaultMessage: 'View more inclusions',
                    description: 'Link text to view more inclusions in suggested option preview',
                  })}
                />
              )}
            </Group>
          </DetailsColumn>

          <PricingColumn direction="vertical" gap={16} verticalAlign="end">
            {!!pricingToShow && <Group direction="vertical" horizontalAlign="start">
              <PriceRowPriceCaption>
                {nightsText}
              </PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="M"
                price={convertToDisplay(pricingToShow.price)}
                memberPrice={memberPricingToShow?.price && convertToDisplay(memberPricingToShow.price)}
                saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                offerConfig={offer.luxPlus}
                horizontalAlign="start"
              />
              <PriceRowTaxesAndFees />
            </Group>}

            {isSameRoomMode && <TextButton
              kind="primary"
              size="small"
              onClick={scrollToPackage}
            >
              {intl.formatMessage({
                id: '7z4-Ngi2aAhJS1Y3B-TV',
                defaultMessage: 'View package',
                description: 'Button text to view package details in suggested option preview',
              })}
            </TextButton>}
          </PricingColumn>
        </Content>
      </ContentContainer>
    </Group>
  </Root>
}

export default SuggestedOptionPreview
